import React, { useCallback, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { chartColors, sensorInterval, themeColors } from '../../../constants';
import {
  makeStyles,
  Theme,
  createStyles,
  Typography,
} from '@material-ui/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IReduxState, IChartProps, ISampledTelemetry } from '../../../types';
import { IPoint } from '../../../types';
import { useWindowSize } from '../../useWindowSize';
import { ActivePointLayer } from '../../ActivePointLayer';
import { CardLayoutGraph } from '../CardLayouts';
import { MeasurementSystem, UnitValueDto } from '../../../rubblemaster_openapi3_typescript-fetch';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tooltipWrapper: {
      position: 'absolute',
      background: themeColors.white,
      opacity: 0.9,
      padding: theme.spacing(2),
      paddingRight: 0,
      color: theme.palette.secondary.main,
      zIndex: 100,
      // top: 10,
      bottom: 0,
      left: -180,
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center',
      [theme.breakpoints.up('sm')]: {
        width: 160,
        // top: 10,
        left: -200,
      },
    },
    tooltipLabel: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    center: {
      display: 'flex',
      height: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      position: 'absolute',
      width: '100%',
      top: theme.spacing(2),
    },
  })
);

export default function EngineUtilizationLine(props: IChartProps) {
  const classes = useStyles();
  const { t } = useTranslation(['translation']);
  const size = useWindowSize();
  const isHybrid = useSelector(
    (state: IReduxState) =>
      // !!state.machineDetails.machine.telemetrySnapshot?.isHybrid
      !!state.machineDetails.machine.isHybrid
  );

  let isSmallScreen = size.width !== undefined && size.width < 500;
  const { isPdfReport } = props;
  // Set isSmallScreen to false for the charts used in the PDF report
  isSmallScreen = isPdfReport === true ? false : isSmallScreen;

  const graphMargin = { top: 50, right: 80, bottom: 100, left: 50 };
  const graphMarginMobile = { top: 25, right: 25, bottom: 150, left: 50 };

  const telemetry = useSelector(
    (state: IReduxState) => state.machineDetails.telemetry
  );
  const telemetryData: any = !!telemetry ? telemetry : [];
  // const graphTickValues = useSelector((state: IReduxState) => state.machineDetails.graphTickValues);
  const graphDateFormat = useSelector(
    (state: IReduxState) => state.machineDetails.graphDateFormat
  );

  // console.log("telemetry");
  // console.log(telemetry);

  // animate point on hover
  const [current, setCurrent] = useState(null);
  const handleHover = useCallback(
    (point) => {
      setCurrent(point);
    },
    [setCurrent]
  );

  // default: false
  /*
  const isJawCrusher = useSelector(
    (state: IReduxState) => state.machineDetails.machine.machineType.isJawCrusher ?? false
  ) 
  */ 
  const hasJawPressure = useSelector(
    (state: IReduxState) => state.machineDetails.machine.machineType.hasJawPressure ?? false
  )

  console.log("hasJawPressure : " + hasJawPressure);


  // #35067: Change Text Average Engine Utilisation to Average Engine Load : Diesel entfernen
  const dieselEngineLoadsTxt: string = t('graph.engineLoads');
  const electricDriveLoadsTxt = t('graph.electricDriveLoads');
  const engineLoadsTxt = t('graph.engineLoads');
  // const jawPressureTxt = t('graph.jawPressure');
  // const auth = useSelector((state: IReduxState) => state.auth);
  // const measurementSystem = auth.user?.measurementSystem ?? MeasurementSystem.Metric;
  // const unit = measurementSystem === MeasurementSystem.Metric ? 'bar' : 'psi';
  
  const averageEnergyLoadsTxt = t('graph.averageEnergyLoads');
  const averageEngineLoadsTxt = t('graph.averageEngineLoads');

  let chartData: any[];
  if (isHybrid) {
    chartData = [
      {
        id: `${dieselEngineLoadsTxt}`,
        // yScaleId: 'left',
        color: chartColors.rmYellow,
        data: telemetryData
          .filter(
            (item: ISampledTelemetry, index: number) =>
              (!item.isEmpty && item.percentageLoadAtCurrentSpeed !== null) ||
              (item.durationInSeconds &&
                item.durationInSeconds > sensorInterval.percentLoad) ||
              // TODO KANM remove when updating NIVO and add min/max to xScale
              // enable first and last items
              index === 0 ||
              index === telemetryData.length - 1
          )
          .map((item: ISampledTelemetry) => {
            const utilization = !!item.percentageLoadAtCurrentSpeed
              ? item.percentageLoadAtCurrentSpeed * 100
              : item.percentageLoadAtCurrentSpeed;
            return {
              x: moment(item.timestamp)
                .utc()
                .local()
                .format('YYYY-MM-DD HH:mm:ss.SSS'),
              y: utilization,
            };
          }),
      },
      {
        id: `${electricDriveLoadsTxt}`,
        // yScaleId: 'left',
        color: chartColors.electricBlue,
        data: telemetryData
          .filter(
            (item: ISampledTelemetry, index: number) =>
              (!item.isEmpty && item.crusherDriveCurrentLoad !== null) ||
              (item.durationInSeconds &&
                item.durationInSeconds > sensorInterval.currentLoadCrusherDrive) ||
              // TODO KANM remove when updating NIVO and add min/max to xScale
              // enable first and last items
              index === 0 ||
              index === telemetryData.length - 1
          )
          .map((item: ISampledTelemetry) => {
            const utilization = !!item.crusherDriveCurrentLoad
              ? item.crusherDriveCurrentLoad
              : item.crusherDriveCurrentLoad;
            return {
              x: moment(item.timestamp)
                .utc()
                .local()
                .format('YYYY-MM-DD HH:mm:ss.SSS'),
              y: utilization,
            };
          }),
      },
    ];
  } else {
    chartData = [
      {
        id: `${engineLoadsTxt}`,
        // yScaleId: 'left',
        color: chartColors.rmYellow,
        data: telemetryData
          .filter(
            (item: ISampledTelemetry, index: number) =>
              (!item.isEmpty && item.percentageLoadAtCurrentSpeed !== null) ||
              (item.durationInSeconds &&
                item.durationInSeconds > sensorInterval.percentLoad) ||
              // TODO KANM remove when updating NIVO and add min/max to xScale
              // enable first and last items
              index === 0 ||
              index === telemetryData.length - 1 
          )
          .map((item: ISampledTelemetry) => {
            const utilization = !!item.percentageLoadAtCurrentSpeed
              ? item.percentageLoadAtCurrentSpeed * 100
              : item.percentageLoadAtCurrentSpeed;
            return {
              x: moment(item.timestamp)
                .utc()
                .local()
                .format('YYYY-MM-DD HH:mm:ss.SSS'),
              y: utilization,
            };
          }),
      },
    ];
  }

  // type ChartPoint = {
  //   x: string;
  //   y: number | undefined;
  // };

  // if (hasJawPressure) {
  //   console.log("")
  //   const jawPressureData = telemetryData
  //     .filter((item: ISampledTelemetry, index: number) =>
  //       (!item.isEmpty && item.jawPressure?.value !== null) ||
  //       ( item.durationInSeconds 
  //         && item.durationInSeconds > sensorInterval.jawPressure
  //       ) ||
  //       index === 0 ||
  //       index === telemetryData.length - 1
  //     )
  //     .map((item: ISampledTelemetry) => {
  //       // const pressure = !!item.jawPressure?.value
  //       const pressure = item.jawPressure?.value !== null && item.jawPressure?.value !== undefined
  //         ? measurementSystem === MeasurementSystem.Metric
  //           ? item.jawPressure.value / 10
  //           : item.jawPressure.value / 100
  //         : null;
  //       return {
  //         x: moment(item.timestamp)
  //         .utc()
  //         .local()
  //         .format('YYYY-MM-DD HH:mm:ss.SSS'),
  //         y: pressure,
  //       };
  //     })
  //     // .sort((a: any, b: any) => moment(a.x).valueOf() - moment(b.x).valueOf())
  //     ;
  
  //   chartData.push({
  //     id: `${jawPressureTxt}`,
  //     color: chartColors.crusherGreen,
  //     data: jawPressureData,
  //   });
  
  //   // 🔍 Ausgabe:
  //   console.log('chartData :', chartData);
  //   console.log('chartData[jawPressureTxt]', jawPressureData);
  // }
  

  const CustomTooltip = (pointData: IPoint) => {
    const { data, serieId, serieColor, x, y } = pointData.point;
    // const isJawPressure: boolean = serieId === jawPressureTxt;
    const value: number = +data.yFormatted;

    const tooltipHeight = 110;
    const tooltipWidth = 180;
    // Dynamische Ausrichtung abhängig von X-Position
    const showRight = x < tooltipWidth * 1.5; // im ersten Drittel
    const tooltipLeft = showRight ? 20 : -tooltipWidth - 20;
    const tooltipTop = y > 300 ? -160 : 20;

    return (
      <div 
        className={classes.tooltipWrapper} 
        style={
          { 
            height: tooltipHeight,
            width: tooltipWidth,
            left: tooltipLeft,
            top: tooltipTop,
           }
        }
        >
        <div className={classes.tooltipLabel}>
          <div style={{ background: `${serieColor}`, width: 10, height: 10 }} />
          <span style={{ color: `${serieColor}`, marginLeft: 8 }}>
            {serieId}
          </span>
        </div>
        <div>
          <span style={{ color: `${serieColor}` }}>{t('graph.date')}: </span>
          <span>{moment(data.x).format('DD.MM.YYYY')}</span>
        </div>
        <div>
          <span style={{ color: `${serieColor}` }}>{t('graph.time')}: </span>
          <span>{moment(data.x).format('HH:mm:ss')}</span>
        </div>
        <div>
          <span style={{ color: `${serieColor}` }}>{`${t('graph.utilization')}: `}</span>
          <span>{value.toFixed(2)} '%'</span>
        </div>
      </div>
    );
  };

  return (
    <CardLayoutGraph
      cardStyle={{
        boxShadow:
          isHybrid && !isPdfReport
            ? `0.25rem 0.25rem 0 0 ${chartColors.electricBlue}`
            : 'none',
      }}
    >
      {telemetryData.length === 0 ||
      telemetryData.percentageLoadAtCurrentSpeed === null ? (
        <div className={classes.center}>
          <Typography align="center" variant="h4">
            {isHybrid
              ? averageEnergyLoadsTxt
              : averageEngineLoadsTxt
            }
          </Typography>
          
          <Typography align="center" variant="h5">
            {t('graph.noData')}
          </Typography>
        </div>
      ) : (
        <React.Fragment>
          {!isSmallScreen && (
            <div className={classes.title}>
              <Typography align="center" variant="h6">
                {averageEnergyLoadsTxt}
              </Typography>
            </div>
          )}

          <ResponsiveLine
            theme={{
              tooltip: {
                container: {
                  background: '#2d374d',
                  color: 'inherit',
                  boxShadow: '0 3px 9px rgba(0, 0, 0, 0.5)',
                },
              },
              axis: {
                legend: {
                  text: {
                    fill: '#fff',
                  },
                },
                ticks: {
                  line: {
                    stroke: 'gray',
                  },
                  text: {
                    fill: '#fff',
                  },
                },
              },
            }}
            data={chartData}
            animate={false}
            colors={(d: any) => d.color}
            lineWidth={2}
            margin={isSmallScreen ? graphMarginMobile : graphMargin}
            xScale={{
              type: 'time',
              format: '%Y-%m-%d %H:%M:%S.%L',
              precision: 'millisecond',
              useUTC: false,
            }}
            xFormat="time:%Y-%m-%d %H:%M:%S.%L"
            yScale={{ type: 'linear', min: 0, max: 150 }}
            axisTop={null}
            axisRight={null}
            enablePoints={true}
            enableArea={true}
            curve="step"
            //@ts-ignore
            layers={[
              'grid',
              'markers',
              'axes',
              'areas',
              'lines',
              'slices',
              'points',
              'mesh',
              'legends',
              () => <ActivePointLayer point={current} />,
            ]}
            //@ts-ignore
            axisBottom={{
              orient: 'bottom',
              tickSize: 10,
              tickPadding: 40,
              tickRotation: 270,
              legendPosition: 'middle',
              format: (tick) => moment(tick).format(graphDateFormat),
              // tickValues: graphTickValues,
            }}
            tooltip={CustomTooltip}
            onMouseEnter={handleHover}
            onMouseMove={handleHover}
            onMouseLeave={handleHover}
            axisLeft={{
              orient: 'left',
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: `${t('graph.engineUtilization')} [${t(
                'graph.%'
              )}]`,
              legendOffset: -40,
              legendPosition: 'middle',
            }}
            enableGridX={false}
            enableCrosshair={false}
            pointSize={2}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabel="x"
            pointLabelYOffset={-12}
            useMesh={true}
            legends={[
              {
                anchor: isSmallScreen ? 'bottom-right' : 'bottom',
                direction: isSmallScreen ? 'column' : 'row',
                justify: false,
                translateX: 0,
                translateY: isSmallScreen ? 140 : 85,
                itemsSpacing: isSmallScreen ? 0 : 10,
                itemDirection: 'left-to-right',
                //itemWidth: isSmallScreen ? 200 : 150,
                // Abstand vergrößert!
                itemWidth: isSmallScreen ? 250 : 200,
                itemHeight: 20,
                itemOpacity: 0.75,
                itemTextColor: '#fff',
                symbolSize: 12,
                symbolShape: 'circle',
                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemBackground: 'rgba(0, 0, 0, .03)',
                      itemOpacity: 1,
                    },
                  },
                ],
              },
            ]}
          />
        </React.Fragment>
      )}
    </CardLayoutGraph>
  );
}
